/**
 * 标量直方图的纯计算实现。
 *
 * WebGL legacy GSplatManager 与 WebGPU Unified scalar 链路共用同一算法，保证两种后端
 * 在分桶、百分位截断与 log 刻度上的结果一致。
 */
import {
  EPSILON_ULTRA,
  GSPLAT_DEFAULT_BIN_COUNT,
  GSPLAT_LOG_SCALE_DIVISOR,
} from '@/shared/constants'
import type { ScalarHistogramResult } from '@/shared/types/gsplat'

/** 参与直方图统计的一段标量样本（对应一个 placement 的 active splat 区间）。 */
export interface ScalarHistogramSampleRange {
  /** 样本在 values 中的起始下标。 */
  readonly baseIndex: number
  /** 该区间的有效样本数量。 */
  readonly count: number
}

export interface ScalarHistogramInput {
  /** 由 GPU 读回的标量值；非有限值会被跳过。 */
  readonly values: ArrayLike<number>
  /** 参与统计的样本区间列表；调用方需自行排除环境与空区间。 */
  readonly ranges: readonly ScalarHistogramSampleRange[]
  /** 底层标量纹理版本号，用于上层失效判断。 */
  readonly textureVersion: number
  readonly binCount?: number
  readonly scale?: 'linear' | 'log'
  readonly percentileRange?: {
    readonly lower?: number
    readonly upper?: number
  }
}

export function computeScalarHistogram(input: ScalarHistogramInput): ScalarHistogramResult | null {
  const binCount = Math.max(1, Math.floor(input.binCount ?? GSPLAT_DEFAULT_BIN_COUNT))
  const bins = new Array<number>(binCount).fill(0)

  let totalActive = 0
  for (const range of input.ranges) {
    if (range.count > 0) totalActive += range.count
  }
  if (totalActive === 0) return null

  const samples = new Float32Array(totalActive)
  let writeIdx = 0
  let min = Number.POSITIVE_INFINITY
  let max = Number.NEGATIVE_INFINITY

  for (const range of input.ranges) {
    for (let offset = 0; offset < range.count; offset++) {
      const scalar = input.values[range.baseIndex + offset]
      if (!Number.isFinite(scalar)) continue
      samples[writeIdx++] = scalar
      min = Math.min(min, scalar)
      max = Math.max(max, scalar)
    }
  }

  const sampleCount = writeIdx
  const textureVersion = input.textureVersion

  if (sampleCount === 0) {
    return {
      min: 0,
      max: 0,
      displayMin: 0,
      displayMax: 0,
      bins,
      sampleCount: 0,
      clippedSampleCount: 0,
      textureVersion,
    }
  }

  // 截断到实际有效长度后原地排序，用于百分位计算
  const validSamples = samples.subarray(0, sampleCount)
  validSamples.sort()

  const lowerPercentile = clampPercentile(input.percentileRange?.lower, 0)
  const upperPercentile = clampPercentile(input.percentileRange?.upper, 100)
  const percentileMin = Math.min(lowerPercentile, upperPercentile)
  const percentileMax = Math.max(lowerPercentile, upperPercentile)
  const scale = input.scale ?? 'linear'

  const displayMin = getPercentileValue(validSamples, percentileMin)
  const displayMax = getPercentileValue(validSamples, percentileMax)
  const scaleTransform = getHistogramScaleTransform(scale, min, max)

  let clippedSampleCount = 0

  if (displayMin === displayMax) {
    for (let i = 0; i < sampleCount; i++) {
      if (validSamples[i] >= displayMin && validSamples[i] <= displayMax) {
        clippedSampleCount++
      }
    }

    bins[0] = clippedSampleCount
    return {
      min,
      max,
      displayMin,
      displayMax,
      bins,
      sampleCount,
      clippedSampleCount,
      textureVersion,
    }
  }

  const scaledDisplayMin = scaleTransform(displayMin)
  const scaledDisplayMax = scaleTransform(displayMax)
  const scaledDisplayRange = scaledDisplayMax - scaledDisplayMin

  if (scaledDisplayRange === 0) {
    bins[0] = sampleCount
    return {
      min,
      max,
      displayMin,
      displayMax,
      bins,
      sampleCount,
      clippedSampleCount: sampleCount,
      textureVersion,
    }
  }

  for (let i = 0; i < sampleCount; i++) {
    const scalar = validSamples[i]
    if (scalar <= displayMin) {
      bins[0]++
      continue
    }

    if (scalar >= displayMax) {
      bins[binCount - 1]++
      continue
    }

    clippedSampleCount++
    const normalized = (scaleTransform(scalar) - scaledDisplayMin) / scaledDisplayRange
    const binIndex = Math.min(binCount - 1, Math.floor(normalized * binCount))
    bins[binIndex]++
  }

  return {
    min,
    max,
    displayMin,
    displayMax,
    bins,
    sampleCount,
    clippedSampleCount,
    textureVersion,
  }
}

export function clampPercentile(value: number | undefined, fallback: number): number {
  if (!Number.isFinite(value)) {
    return fallback
  }

  return Math.min(100, Math.max(0, value ?? fallback))
}

function getPercentileValue(sortedSamples: ArrayLike<number>, percentile: number): number {
  if (sortedSamples.length === 0) {
    return 0
  }

  if (sortedSamples.length === 1) {
    return sortedSamples[0]
  }

  const clamped = Math.min(100, Math.max(0, percentile))
  const position = (clamped / 100) * (sortedSamples.length - 1)
  const lowerIndex = Math.floor(position)
  const upperIndex = Math.ceil(position)

  if (lowerIndex === upperIndex) {
    return sortedSamples[lowerIndex]
  }

  const ratio = position - lowerIndex
  return sortedSamples[lowerIndex] * (1 - ratio) + sortedSamples[upperIndex] * ratio
}

function getHistogramScaleTransform(
  scale: 'linear' | 'log',
  min: number,
  max: number
): (value: number) => number {
  if (scale !== 'log') {
    return (value: number) => value
  }

  const range = Math.max(0, max - min)
  const offset = Math.max(range / GSPLAT_LOG_SCALE_DIVISOR, EPSILON_ULTRA)

  return (value: number) => {
    const shifted = Math.max(0, value - min)
    return Math.log10(shifted + offset)
  }
}
